/**
 * Which design the public asset frontend uses: the new one or the classic one.
 *
 * The new design is the default everywhere, production included, with no
 * server config, hostname or environment consulted. The only way to see the
 * classic design (`home/AssetFrontendHome.tsx`, `AssetViewClassic.tsx`) is a
 * browser whose localStorage `design` entry is exactly `old`.
 *
 * A URL command sticks across reloads: open any asset frontend URL with
 * `?design=old` to store that entry, or `?design=new` to remove it. Anything
 * else in the `design` param is not a command and leaves storage alone.
 *
 * Read once at module load, before React mounts, same idiom as
 * `custolink-newdesign/flag.ts`: a value that arrived a tick later would
 * mount one shell and then throw it away.
 */
export const DESIGN_STORAGE_KEY = 'design'
export const DESIGN_OPT_OUT_VALUE = 'old'
export const DESIGN_QUERY_PARAM = 'design'

/** The subset of the Storage API the resolver needs; lets tests fake it. */
export type DesignStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/**
 * Pure resolver: true means render the new design. `search` is a URL query
 * string (e.g. `location.search`); `storage` is `localStorage` or `null`
 * (SSR / storage unavailable). Every storage access is best-effort: a
 * throwing storage never lets its exception escape, and the URL command
 * still decides the return value for this page even when storage is null
 * or broken.
 */
export function resolveAssetFrontendNewDesign(search: string, storage: DesignStorage | null): boolean {
  const param = new URLSearchParams(search).get(DESIGN_QUERY_PARAM)

  if (param === DESIGN_OPT_OUT_VALUE) {
    try {
      storage?.setItem(DESIGN_STORAGE_KEY, DESIGN_OPT_OUT_VALUE)
    } catch {
      /* private mode / storage disabled - the URL still wins for this page */
    }
    return false
  }

  if (param === 'new') {
    try {
      storage?.removeItem(DESIGN_STORAGE_KEY)
    } catch {
      /* private mode / storage disabled - the URL still wins for this page */
    }
    return true
  }

  if (!storage) return true

  try {
    return storage.getItem(DESIGN_STORAGE_KEY) !== DESIGN_OPT_OUT_VALUE
  } catch {
    return true
  }
}

function resolve(): boolean {
  if (typeof window === 'undefined') return resolveAssetFrontendNewDesign('', null)

  let storage: DesignStorage | null = null
  try {
    storage = window.localStorage
  } catch {
    /* private mode / storage disabled */
    storage = null
  }

  return resolveAssetFrontendNewDesign(window.location.search, storage)
}

const newDesign = resolve()

/** Sync read, for module scope and non-React code. */
export function isAssetFrontendNewDesign(): boolean {
  return newDesign
}

/** True when the new design should render. Constant for the lifetime of the page. */
export function useAssetFrontendDesign(): boolean {
  return newDesign
}
